import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import type { ProdutoCatalogo } from '../api';
import { formatarBRL } from '../lib/formato';
import { precoBaseProduto } from '../lib/preco-produto';

interface Props {
  produto: ProdutoCatalogo;
  /** Props do Typography do VALOR (variant, color, sx…). */
  valor?: TypographyProps;
  /** Ajuste fino do rótulo "a partir de" (cor/tamanho). Por padrão herda a cor
   *  do contexto com opacidade — funciona tanto no card quanto no fundo escuro
   *  do tablet. */
  rotuloSx?: SxProps<Theme>;
  /** sx do wrapper (alinhamento, margem). */
  sx?: SxProps<Theme>;
}

/**
 * Preço de exibição de um produto no cardápio/propagandas. Mostra o piso
 * calculado por {@link precoBaseProduto} e, quando o total pode variar, um
 * rótulo discreto "a partir de" acima do valor — evitando passar falsa
 * impressão quando o produto só ganha preço pelos passos.
 */
export function PrecoProduto({ produto, valor, rotuloSx, sx }: Props): JSX.Element {
  const { label, valor: preco, valorOriginal } = precoBaseProduto(produto);

  return (
    <Box sx={sx}>
      {label && (
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: '0.72em', lineHeight: 1.15, opacity: 0.7, ...rotuloSx }}
        >
          {label}
        </Typography>
      )}
      {valorOriginal !== null && (
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.75em',
            lineHeight: 1.1,
            opacity: 0.55,
            textDecoration: 'line-through',
            ...rotuloSx,
          }}
        >
          {formatarBRL(valorOriginal)}
        </Typography>
      )}
      <Typography {...valor}>{formatarBRL(preco)}</Typography>
    </Box>
  );
}
